type ScriptSource = 'first-party' | 'external-origin' | 'opaque-no-origin';

interface BrowserErrorProperties {
  browser_error_source: ScriptSource;
  browser_error_script_path?: string;
  browser_error_line?: number;
  browser_error_column?: number;
  browser_error_build_sha?: string;
  $exception_list?: Array<{
    type: string;
    value: string;
    mechanism: { type: string; handled: boolean; synthetic: boolean };
  }>;
}

function scriptSource(filename: string, origin?: string): BrowserErrorProperties {
  if (!filename || !/^(?:https?:\/\/|\/)/i.test(filename)) {
    return { browser_error_source: 'opaque-no-origin' };
  }
  let url: URL;
  try {
    url = new URL(filename, origin);
  } catch {
    return { browser_error_source: 'opaque-no-origin' };
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return { browser_error_source: 'opaque-no-origin' };
  }
  const firstParty = url.origin === origin;
  const properties: BrowserErrorProperties = {
    browser_error_source: firstParty ? 'first-party' : 'external-origin',
  };
  // Only retain our generated Vite bundle paths. External URLs, arbitrary
  // paths, credentials, queries and fragments can contain applicant data.
  if (
    firstParty && !url.username && !url.password
    && /^\/assets\/[A-Za-z0-9_-]+-[A-Za-z0-9_-]{8}\.js$/.test(url.pathname)
  ) {
    properties.browser_error_script_path = url.pathname;
  }
  return properties;
}

export function browserErrorDiagnostic(
  event: Pick<ErrorEvent, 'error' | 'filename' | 'lineno' | 'colno'>,
  origin?: string,
  buildSha?: string | null,
) {
  const properties = scriptSource(event.filename, origin);
  if (Number.isSafeInteger(event.lineno) && event.lineno > 0) {
    properties.browser_error_line = event.lineno;
  }
  if (Number.isSafeInteger(event.colno) && event.colno > 0) {
    properties.browser_error_column = event.colno;
  }
  if (buildSha && /^[a-f0-9]{40}$/i.test(buildSha)) {
    properties.browser_error_build_sha = buildSha;
  }
  if (event.error != null) return { exception: event.error, properties };

  // The browser did not expose an exception. Do not invent a source stack
  // pointing at this capture listener, or copy arbitrary message content.
  const exception = new Error('Browser script error (source unavailable)');
  exception.stack = '';
  // PostHog replaces even an empty Error.stack with its own synthetic stack.
  // Its capture properties override that generated exception list, so report
  // this explicitly as synthetic with no originating frames.
  properties.$exception_list = [{
    type: exception.name,
    value: exception.message,
    mechanism: { type: 'generic', handled: true, synthetic: true },
  }];
  return { exception, properties };
}
